import type { Entity } from '../entity';
import type { AgentHarnessRun } from '../../ai/agent-harness/agent.run';
import type { AgentHarnessRunStatus } from '../../ai/agent-harness/agent.run.enums';
import type { AgentHarnessEventType } from '../../ai/agent-harness/agent.event.enums';

export const EXTENSION_RUN_DELIVERY_PROCESS = 'ExtensionRunDelivery';

/**
 * The event type of the queued entry that resumes a Waiting workflow run. It joins the Job lane like any event, so a resumed run
 * takes its turn behind whatever is queued; it never starts a new run.
 */
export const EXTENSION_WORKFLOW_RESUME_EVENT = 'Workflow Resume';

/** `lastFailureCause` codes the extension run delivery writes; the run status mapping reads them back. */
export enum ExtensionRunDeliveryCause {
    Invalid = 'INVALID_EXTENSION_RUN',
    Pending = 'EXTENSION_RUN_PENDING',
    Paused = 'EXTENSION_RUN_PAUSED',
    Denied = 'EXTENSION_RUN_DENIED',
    DeploymentChanged = 'EXTENSION_DEPLOYMENT_CHANGED',
    DeploymentIncompatible = 'EXTENSION_DEPLOYMENT_INCOMPATIBLE',
}

export enum ExtensionRunControlKind {
    Pause = 'pause',
    Resume = 'resume',
    Cancel = 'cancel',
    Retry = 'retry',
}

export interface ExtensionRunControlRequest {
    runId: string;
    requestId: string;
    kind: ExtensionRunControlKind;
    reason: string;
}

export interface ExtensionRunControlOperation extends ExtensionRunControlRequest {
    requestedBy: Entity;
    requestedAt: Date;
    resultRunId: string;
    resultStatus: AgentHarnessRunStatus;
}

export interface ExtensionRunHistoryRequest {
    agentId: string;
    entityId: string;
    cursor?: string;
    limit?: number;
}

/** What a workflow run is doing at the step its position names. */
export enum ExtensionRunPositionState {
    Running = 'running',
    Waiting = 'waiting',
    Failed = 'failed',
    Done = 'done',
}

/**
 * Where a workflow run got up to, as its runtime last reported it (`ExtensionRunPositionReport`). Only a report with a higher
 * `sequence` than the one that set it replaces it, so a late or replayed older report never moves it backwards.
 */
export interface ExtensionRunPosition {
    /** The interpreter key: the step id prefixed with its loop iterations (`loop[2].note`). */
    stepKey: string;
    stepId: string;
    /** The workflow step type, a `WorkflowStepType` value. */
    type: string;
    state: ExtensionRunPositionState;
    /** Inside loops, the iteration index of each enclosing loop, outermost first. */
    iteration?: number[];
    /** When WorkBuddy received the report that set it. */
    at: Date;
    /** The report's `sequence`. */
    sequence: number;
}

/** How far one For each has got, so a view shows "item 3 of 5" without reading every iteration's events. */
export interface ExtensionRunLoopProgress {
    /** The For each step's key; a loop nested in another has one entry per outer iteration (`outer[1].inner`). */
    key: string;
    /** Items the loop iterates over; 0 until it has decided. */
    total: number;
    /** Iterations finished. */
    done: number;
    /** Iterations that failed; a failure ends the run, so at most 1. */
    failed: number;
}

/**
 * The body of `POST /api/extensions/runs/:runId/position`: the runtime's best-effort report of where a workflow run is,
 * sent at most once a second and on every milestone. Not an event: WorkBuddy writes it straight onto the run, never to
 * `agent_harness_event`. `sequence` numbers the runtime's position calls in walk order, the same on every replay.
 */
export interface ExtensionRunPositionReport {
    runId: string;
    applicationId: string;
    sequence: number;
    position: Omit<ExtensionRunPosition, 'at' | 'sequence'> & { loops: ExtensionRunLoopProgress[] };
}

export interface ExtensionRunHistoryItem {
    run: Pick<AgentHarnessRun, '_id' | 'agentName' | 'subscriptionId' | 'entityType' | 'entityId' | 'eventType' | 'status' | 'definitionRevision' | 'capabilityVersion' | 'runtimeDeploymentVersion' | 'createdAt' | 'updatedAt' | 'paused' | 'retryOfRunId' | 'failureReason' | 'position' | 'loops'>;
    source: Entity;
    events: { eventId: string; type: AgentHarnessEventType; payload?: Record<string, unknown>; createdAt?: Date }[];
    delivery: { attempts: number; status?: string; lastError?: string; updatedAt?: Date };
    operations: ExtensionRunControlOperation[];
}

export interface ExtensionRunHistoryPage {
    items: ExtensionRunHistoryItem[];
    hasMore: boolean;
    nextCursor: string | null;
}

export interface ExtensionRunControlResult {
    runId: string;
    resultRunId: string;
    status: AgentHarnessRunStatus;
}

export enum ExtensionRunSocketSubject {
    History = 'GetExtensionRunHistoryQuery',
    Control = 'ControlExtensionRunCommand',
}
